import React, { useEffect, useState } from 'react';
import { formatItemAmount, importFromProcore, listFromProcore, sendToProcore } from '../procoreActions';
import {
  asList,
  getJson,
  integrationError,
  integrationLabel,
  isErpIntegration,
  isProjectToolIntegration,
  isSyncableIntegration,
  sendKindFor,
} from '../helpers';

function laborCostImportKeys(row) {
  const timesheet = row?.procore_timesheet_id;
  const employee = row?.employee_id || row?.employee_vuid;
  const keys = [];
  if (timesheet && employee) keys.push(`${timesheet}:${employee}`);
  if (timesheet) keys.push(String(timesheet));
  return keys;
}

function journalTitle(entry) {
  return entry.journal_number || entry.reference_number || entry.vuid || 'Labor journal';
}

function journalSubtitle(entry) {
  return [entry.description, formatItemAmount(entry.total_amount)].filter(Boolean).join(' · ');
}

export default function LaborCostSyncModal({ projectVuid, onClose, onComplete }) {
  const [integrations, setIntegrations] = useState([]);
  const [procoreMappingIds, setProcoreMappingIds] = useState(new Set());
  const [journals, setJournals] = useState([]);
  const [journalsLoaded, setJournalsLoaded] = useState(false);
  const [integrationVuid, setIntegrationVuid] = useState('');
  const [direction, setDirection] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('init');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];
  const sendAllowed = Boolean(sendKindFor(integration, 'journal_entry'));
  const receiveAllowed = isProjectToolIntegration(integration);

  const resetChoice = () => {
    setDirection('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    let cancelled = false;
    setBusy('init');
    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/external-system-ids', { object_type: 'labor_cost', project_vuid: projectVuid }).catch(() => []),
    ])
      .then(([integrationRows, mappingRows]) => {
        if (cancelled) return;
        const nextIntegrations = asList(integrationRows).filter((row) => {
          if (String(row.status || '').toLowerCase() !== 'active') return false;
          return isSyncableIntegration(row);
        });
        setIntegrations(nextIntegrations);
        setIntegrationVuid(nextIntegrations[0]?.vuid || '');
        setProcoreMappingIds(
          new Set(asList(mappingRows).map((row) => String(row.external_id || '')).filter(Boolean))
        );
      })
      .catch((err) => {
        if (!cancelled) setFlash({ type: 'err', text: integrationError(err) });
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection) => {
    if (!integration) return;
    setBusy('load');
    setFlash(null);
    try {
      if (nextDirection === 'receive' && receiveAllowed) {
        const result = await listFromProcore('labor-costs', integration.vuid, projectVuid);
        const next = (result.items || [])
          .filter((row) => !laborCostImportKeys(row.raw).some((key) => procoreMappingIds.has(key)))
          .map((row) => ({
            id: row.id,
            title: row.title,
            subtitle: [row.subtitle, formatItemAmount(row.amount)].filter(Boolean).join(' · '),
            importKind: 'labor-costs',
            listItem: row,
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
        if (result.warnings?.length) setFlash({ type: 'err', text: result.warnings.slice(0, 4).join('\n') });
        return;
      }
      if (nextDirection === 'send' && sendAllowed) {
        let localJournals = journals;
        if (!journalsLoaded) {
          const journalRows = asList(
            await getJson('/api/journal-entries', { project_vuid: projectVuid }).catch(() => [])
          );
          localJournals = journalRows.filter(
            (row) => row.transaction_type === 'labor_cost' || row.reference_type === 'labor_cost'
          );
          setJournals(localJournals);
          setJournalsLoaded(true);
        }
        const next = localJournals
          .filter((entry) => !entry.is_synced)
          .map((entry) => ({
            id: entry.vuid,
            title: journalTitle(entry),
            subtitle: journalSubtitle(entry),
            sendKind: sendKindFor(integration, 'journal_entry', 'journal-entry'),
            payload: { journal_entry_vuid: entry.vuid },
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
        return;
      }
      setItems([]);
      setSelected([]);
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    loadItems(nextDirection);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (direction === 'receive') {
        const result = await importFromProcore(
          'labor-costs',
          integration.vuid,
          projectVuid,
          chosen.map((row) => row.listItem)
        );
        sent += chosen.length;
        (result.errors || []).forEach((item) => errors.push(item));
      } else {
        for (const row of chosen) {
          try {
            await sendToProcore(integration.vuid, row.sendKind, row.payload);
            sent += 1;
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const emptyMessage =
    isErpIntegration(integration) && direction === 'send'
      ? `No unsynced posted labor cost journals. Post labor costs first, then send them to ${integrationLabel(integration)}.`
      : isProjectToolIntegration(integration) && direction === 'receive'
        ? 'No unsynced timesheets for this project.'
        : 'No unsynced records for this integration.';

  return (
    <div className="pc-modal-backdrop" onClick={() => busy !== 'load' && busy !== 'sync' && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync labor costs</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then retrieve timesheets from the project tool or send posted labor journals to the ERP.
          </p>
          {busy === 'init' ? <div className="pc-empty">Loading integrations…</div> : null}
          {busy !== 'init' && integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : null}
          {busy !== 'init' && integrations.length > 0 ? (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <div className="pc-sync-options">
                  <button
                    type="button"
                    className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                    disabled={!sendAllowed}
                    onClick={() => pickDirection('send')}
                  >
                    <strong>Send</strong>
                    <span>
                      {isErpIntegration(integration)
                        ? `Push this project’s posted labor cost journals to ${integrationLabel(integration)}.`
                        : `Labor cost journals are sent to the ERP, not ${integrationLabel(integration) || 'this integration'}.`}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                    disabled={!receiveAllowed}
                    onClick={() => pickDirection('receive')}
                  >
                    <strong>Receive</strong>
                    <span>
                      {isProjectToolIntegration(integration)
                        ? `Pull unsynced timesheets from ${integrationLabel(integration)} into this project.`
                        : 'Labor costs are retrieved from the project tool. There is no retrieve from the ERP.'}
                    </span>
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && busy !== 'load' && busy !== 'init' ? (
            items.length === 0 ? (
              <div className="pc-empty">{emptyMessage}</div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={busy === 'load' || busy === 'sync'}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={busy === 'load' || busy === 'sync' || busy === 'init' || !direction || !selected.length}
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
